/**
 * Tools showcase - Figma `6377:86989` on the catalogue, `3883:70427` on the home page
 * (and `6377:87206`, `6377:87231`, `6377:88703`).
 *
 * The BeeOffice / Cytric / MY-WCP switcher is one section with a tab list, not three
 * sections - see docs/section-inventory.md.
 *
 * **`tabbed-info` was merged into this on 2026-09-04.** Its fields were a strict subset
 * of these and the node it cited (`6377:87280`) shows no tabs at all, so it was a second
 * tab section with no design of its own. The design draws it as:
 *
 *   eyebrow, heading, lead
 *   [ tool pills ...........................................  call to action ]
 *   ┌ grey ground ────────────────────────────────────────────────────────────┐
 *   │ heading + button   │   photograph   │   description                     │
 *   └──────────────────────────────────────────────────────────────────────────┘
 *
 * The three panel columns are the tab's `heading`, `image` and `body` - one field each,
 * which is why the arrangement is worth spelling out: it is a layout, not three new
 * fields. The design's third column also carries a row of transport icons (air, rail,
 * hotel); there is no field for them and inventing one for a single tab is not worth it.
 *
 * The tabs are a real ARIA tab set - `role="tablist"`, arrow keys, one tab stop - rather
 * than a row of buttons with `aria-pressed`, which is what this section used to be.
 */
import { useId, useRef, useState } from 'react';
import { Body, CtaLink, Eyebrow, Heading, Lead, SectionShell } from '../primitives';
import type { ToolsShowcaseSection } from '../../types/sections';
import { Image, SIZES } from '../../components/ui/Image';

export default function ToolsShowcase({ data }: { data: ToolsShowcaseSection }) {
  const tabs = data.tabs ?? [];
  const [active, setActive] = useState(0);
  const current = tabs[active];
  const id = useId();
  const list = useRef<HTMLDivElement>(null);

  // Roving tabindex: left/right move between tabs, Home/End jump to the ends.
  function onKeyDown(event: React.KeyboardEvent) {
    const last = tabs.length - 1;
    const next =
      event.key === 'ArrowRight' ? (active === last ? 0 : active + 1)
      : event.key === 'ArrowLeft' ? (active === 0 ? last : active - 1)
      : event.key === 'Home' ? 0
      : event.key === 'End' ? last
      : null;
    if (next === null) return;
    event.preventDefault();
    setActive(next);
    list.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
  }

  return (
    <SectionShell settings={data.settings} name="tools-showcase">
      {data.eyebrow ? <Eyebrow value={data.eyebrow} /> : null}
      {data.heading ? <Heading>{data.heading}</Heading> : null}
      {data.lead ? <Lead>{data.lead}</Lead> : null}

      {/* The pills and the call to action share a line, as in 3883:70433. */}
      {tabs.length || data.cta ? (
        <div className="mt-4 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          {tabs.length ? (
            <div ref={list} role="tablist" aria-label={data.heading ?? undefined} className="flex flex-wrap gap-4" onKeyDown={onKeyDown}>
              {tabs.map((tab, i) => (
                <button
                  key={tab.key ?? i}
                  type="button"
                  role="tab"
                  id={`${id}-tab-${i}`}
                  aria-selected={i === active}
                  aria-controls={`${id}-panel-${i}`}
                  tabIndex={i === active ? 0 : -1}
                  onClick={() => setActive(i)}
                  className={`rounded-full border px-5 py-2 text-body-sm transition ${
                    i === active
                      ? 'border-ink-strong bg-ink-strong text-white'
                      : 'border-grey-300 bg-surface text-ink hover:border-ink-strong'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          ) : <span />}

          {data.cta ? (
            <div className="flex w-fit items-center rounded-full bg-surface py-2 pl-6 pr-2 lg:shrink-0">
              <CtaLink value={data.cta} />
            </div>
          ) : null}
        </div>
      ) : null}

      {current ? (
        <div
          role="tabpanel"
          id={`${id}-panel-${active}`}
          aria-labelledby={`${id}-tab-${active}`}
          tabIndex={0}
          // Three columns when the tab has a screenshot, two when it does not - which is
          // what `tabbed-info` authored before it was merged in here, and a fixed
          // `grid-cols-3` would have left a hole where its photograph never was.
          className={`grid gap-4 rounded-l bg-surface-muted p-4 ${
            current.image ? 'lg:grid-cols-3' : 'lg:grid-cols-2'
          }`}
        >
          {/* Left: the tool's name and the way in. */}
          <div className="flex min-h-[400px] flex-col justify-between gap-10 rounded-m bg-surface p-8">
            {current.heading ? <h3 className="text-h4 text-ink">{current.heading}</h3> : null}
            {current.cta ? <CtaLink value={current.cta} /> : null}
          </div>

          {/* Middle: the screenshot. */}
          {current.image ? (
            <figure className="m-0 min-h-[400px] overflow-hidden rounded-m bg-surface-sunken">
              <Image
                media={current.image}
                alt={current.image.alternativeText ?? current.heading ?? ''}
                sizes={SIZES.third}
                className="h-full w-full object-cover"
              />
            </figure>
          ) : null}

          {/* Right: what it does. */}
          {current.body ? (
            <div className="min-h-[400px] rounded-m bg-surface p-8">
              <Body>{current.body}</Body>
            </div>
          ) : null}
        </div>
      ) : null}
    </SectionShell>
  );
}
